
import React from 'react';
import { 
  BookOpen, Sparkles, Zap, Mail, MapPin, 
  CreditCard, ShieldCheck, Database, MousePointer2,
  CheckCircle2, Info, ArrowRight, Scale, ShieldAlert
} from 'lucide-react';

const HelpGuide: React.FC = () => {
  const Step = ({ number, title, desc, icon: Icon }: any) => (
    <div className="flex gap-6 relative group">
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-black text-lg border border-blue-100 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
        {Icon ? <Icon size={20} /> : number}
      </div>
      <div className="pt-2">
        <h4 className="font-bold text-slate-800 mb-1">{title}</h4>
        <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, desc, color }: any) => (
    <div className="p-6 bg-white border border-slate-100 rounded-[2rem] hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300">
      <div className={`w-12 h-12 rounded-2xl ${color} flex items-center justify-center mb-4 shadow-sm`}>
        <Icon size={22} className="text-white" />
      </div>
      <h4 className="font-bold text-slate-800 mb-2">{title}</h4>
      <p className="text-[11px] text-slate-500 leading-relaxed">{desc}</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      {/* 头部介绍 */}
      <section className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100">
          <BookOpen size={12} /> 用户手册
        </div>
        <h2 className="text-4xl font-black text-slate-900 tracking-tight">如何使用 Identity Pro？</h2>
        <p className="text-slate-500 max-w-lg mx-auto text-sm">
          欢迎使用全球领先的虚拟身份编织系统。本手册将带您快速掌握从生成、同步到批量导出的完整流程。
        </p>
      </section>

      {/* 法律合规专栏 (New) */}
      <section className="bg-orange-50 border border-orange-200 p-8 rounded-[2.5rem] relative overflow-hidden">
        <div className="absolute top-0 right-0 p-10 text-orange-200 pointer-events-none opacity-20">
          <ShieldAlert size={120} />
        </div>
        <h3 className="text-lg font-black text-orange-900 mb-6 flex items-center gap-3">
          <Scale size={24} /> 免责声明与法律合规
        </h3>
        <div className="space-y-4 relative z-10">
          <p className="text-xs text-orange-800 leading-relaxed font-bold">
            Identity Pro 是一个专为开发者和研究人员设计的技术实验平台。在使用过程中，您必须严格遵守以下规则：
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <li className="flex items-start gap-3 p-4 bg-white/60 rounded-2xl border border-orange-100">
              <CheckCircle2 size={16} className="text-orange-500 mt-0.5" />
              <span className="text-[11px] text-orange-900 leading-relaxed italic">本站数据均由 AI 随机算法生成，无任何真实个人对应。</span>
            </li>
            <li className="flex items-start gap-3 p-4 bg-white/60 rounded-2xl border border-orange-100">
              <CheckCircle2 size={16} className="text-orange-500 mt-0.5" />
              <span className="text-[11px] text-orange-900 leading-relaxed italic">严禁用于任何非法注册、欺诈、电信网络诈骗等违法活动。</span>
            </li>
            <li className="flex items-start gap-3 p-4 bg-white/60 rounded-2xl border border-orange-100">
              <CheckCircle2 size={16} className="text-orange-500 mt-0.5" />
              <span className="text-[11px] text-orange-900 leading-relaxed italic">用户需对自身的生成记录及数据导出后的用途负全部法律责任。</span>
            </li>
            <li className="flex items-start gap-3 p-4 bg-white/60 rounded-2xl border border-orange-100">
              <CheckCircle2 size={16} className="text-orange-500 mt-0.5" />
              <span className="text-[11px] text-orange-900 leading-relaxed italic">本系统仅限用于非生产环境下的前端 UI 演示与数据库填充。</span>
            </li>
          </ul>
        </div>
      </section>

      {/* 快速上手步进 */}
      <section className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full -mr-32 -mt-32 -z-10 opacity-50"></div>
        <h3 className="text-xl font-black text-slate-900 mb-10 flex items-center gap-3">
          <Zap size={24} className="text-orange-500" /> 3 步开启身份演算
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
          <Step 
            number="1" 
            title="配置偏好" 
            desc="在左侧面板选择目标国家（如美国、日本）、性别倾向、年龄范围及内核模型。" 
          />
          <Step 
            number="2" 
            title="一键生成" 
            desc="点击“生成随机身份”按钮。系统将立即调用 Gemini AI 或本地算法进行逻辑演算。" 
          />
          <Step 
            number="3" 
            title="提取数据" 
            desc="在卡片中点击分享图标可复制全文，或进入“批量提取”页面导出 JSON/CSV 文件。" 
          />
        </div>
      </section>

      {/* 功能详解 */}
      <section className="space-y-8">
        <h3 className="text-xl font-black text-slate-900 ml-2">特色功能详解</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FeatureCard 
            icon={Mail} 
            title="真实临时邮箱" 
            desc="生成的邮箱均可通过内置按钮一键访问 Mail7 收件箱，支持接收验证码。" 
            color="bg-blue-500"
          />
          <FeatureCard 
            icon={MapPin} 
            title="地理位置解析" 
            desc="点击地址图标可跳转至 Google Maps 查看具体街道位置，数据高度拟真。" 
            color="bg-indigo-500"
          />
          <FeatureCard 
            icon={CreditCard} 
            title="财务信息模拟" 
            desc="生成的卡号符合 Luhn 算法，包含 CVV 及有效期，适用于沙盒支付测试。" 
            color="bg-slate-800"
          />
          <FeatureCard 
            icon={Database} 
            title="批量数据流" 
            desc="在批量提取页面可对历史数据进行勾选，支持导出为 CSV 或 JSON 格式。" 
            color="bg-purple-500"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-900 p-10 rounded-[3rem] text-white">
        <h3 className="text-xl font-black mb-8 flex items-center gap-3">
          <Info size={24} className="text-blue-400" /> 常见问题
        </h3>
        <div className="space-y-6">
          <div className="pb-6 border-b border-white/10">
            <h4 className="font-bold text-blue-300 mb-2 italic">Q: 注册后的初始额度是多少？</h4>
            <p className="text-xs text-slate-400 leading-relaxed">A: 普通用户注册后拥有 5 次 Pro 生成额度。额度耗尽后系统会自动降级为本地生成模式。您可以联系管理员重置额度。</p>
          </div>
          <div className="pb-6 border-b border-white/10">
            <h4 className="font-bold text-blue-300 mb-2 italic">Q: 为什么 AI 生成偶尔会报错？</h4>
            <p className="text-xs text-slate-400 leading-relaxed">A: AI 模式依赖云端 API。如果出现报错，请检查您的网络环境（尤其是跨国网络连接），或检查 API Key 是否被配置正确。</p>
          </div>
          <div>
            <h4 className="font-bold text-blue-300 mb-2 italic">Q: 我的隐私安全吗？</h4>
            <p className="text-xs text-slate-400 leading-relaxed">A: 我们不对您的生成数据进行二次收集。清空系统设置中的历史记录将永久销毁服务器上的相关备份。</p>
          </div>
        </div>
      </section>

      <div className="text-center pt-8">
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">End of guide · Identity Pro v1.2</p>
      </div>
    </div>
  );
};

export default HelpGuide;
